// Contient les fonctions et configs de base utilisées par les services
// Chaque fonction fait appel à une fonction try commune

import LoggerService,{formatTrace} from '../../LoggerService';
import RefreshTokenSingleton from '../RefreshTokenSingleton';

import I_ERROR from '../interfaces/I_ERROR';

export const CONFIG_API_RESPONSES = {
  RESPONSE_CODE_SUCCESS: 200,
  RESPONSE_CODE_CREATE: 201,
  RESPONSE_CODE_DELETE: 204,
  RESPONSE_CODE_INCOMPLETE: 400,
  RESPONSE_CODE_UNAUTHORIZED: 401,
  RESPONSE_CODE_FORBIDDEN: 403,
  RESPONSE_CODE_NOTFOUND: 404,
  RESPONSE_CODE_CONFLICT: 409,
  RESPONSE_CODE_VALIDATION_ERROR: 422,
};

const logger = LoggerService.getInstance();

/**
 * Renvoie true si on a bien des tokens
 * @param access_token
 * @param refreshToken
 */
export const _checkTokens = async (accessToken?:string,refreshToken?:string):Promise<boolean> => {
  if (!accessToken || !refreshToken) {
    const error = {
      error: true,
      errorCode: 401,
      errorText: 'Missing accessToken or refreshToken',
      errorMessage: 'Missing accessToken or refreshToken to perform API request',
    };
    return Promise.reject(error);
  }
  return true;
};

/**
 * Si la réponse est ok, renvoie true, sinon rejette la Promise en renvoyant un objet décrivant l'erreur
 * @param {Response} response
 * @returns
 */
export const _checkErrors = async (response:Response):Promise<boolean> => {
  if (response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_INCOMPLETE ||
      response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_UNAUTHORIZED ||
      response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_FORBIDDEN ||
      response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_NOTFOUND ||
      response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_CONFLICT) {
    const responseBody:I_ERROR = await response.json();
    return Promise.reject({
      error: true,
      errorCode: response.status,
      errorText: response.statusText,
      errorMessage: responseBody.detail,
      errorBody: responseBody,
    });
  }
  if (response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_VALIDATION_ERROR) {
    const responseBody:I_ERROR = await response.json();
    const detail = responseBody?.detail !== undefined ? (typeof responseBody.detail === 'string' ? responseBody.detail : JSON.stringify(responseBody.detail)) : '';
    return Promise.reject({
      error: true,
      errorCode: response.status,
      errorText: response.statusText,
      errorMessage: `Invalid format : ${detail}`,
      errorBody: responseBody,
    });
  }
  // autres codes
  if (!response.ok) {
    const responseBody:I_ERROR = await response.json();
    const detail = responseBody?.detail !== undefined ? (typeof responseBody.detail === 'string' ? responseBody.detail : JSON.stringify(responseBody.detail)) : '';
    return Promise.reject({
      error: true,
      errorCode: response.status,
      errorText: detail,
      errorMessage: `Unkwnown response code ${response.status} : ${detail}`,
      errorBody: responseBody,
    });
  }
  return true;
};

interface I_TRY_OR_REFRESH_PARAMS {
  request: any, // fonction api async
  accessToken: string,
  refreshToken: string,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
/**
 * Tente une premiere requete endpoint, et si on a une 401, tente un refresh token via le singleton,
 * puis retente la requete endpoint
 * @param {I_TRY_OR_REFRESH_PARAMS} obj
 * @returns
 */
export const _tryOrRefresh = async ({request,accessToken,refreshToken,dispatch,state}:I_TRY_OR_REFRESH_PARAMS):Promise<Response> => {
  const DEBUG_TRY = false;  // pour reactiver rapidement les logs de la fonction

  // lance une premiere fois la requete endpoint
  if (DEBUG_TRY) {
    logger.trace(...formatTrace('ApiServices - _tryOrRefresh - First try...'));
  }
  let response = await request(accessToken);
  if (DEBUG_TRY) {
    logger.trace(...formatTrace('ApiServices - _tryOrRefresh - (1) returned response',response));
  }
  // au lieu de faire le checkErrors generique, on veut faire une action specifique si 401
  if (response.status === CONFIG_API_RESPONSES.RESPONSE_CODE_UNAUTHORIZED) {
    // si la reponse 401 est arrivee apres qu'un autr composant ait fini son refresh, il faut reessayer avec le nouveau token
    // donc on doit relire le token courant et le comparer à celui de la fonction
    if (state.accessToken !== accessToken) {
      if (DEBUG_TRY) {
        logger.trace(...formatTrace('ApiServices - _tryOrRefresh - access token has changed during request. Try again with the new one...'));
      }
      const newAccessToken:string = state.accessToken;
      // on reessaye l'action
      response = await request(newAccessToken);
      if (DEBUG_TRY) {
        logger.trace(...formatTrace('ApiServices - _tryOrRefresh - (2) returned response',response));
      }
      return response;
    }
    else {
      if (DEBUG_TRY) {
        logger.trace(...formatTrace('ApiServices - _tryOrRefresh - access token is expired, getting new one...'));
      }
      const refresher = RefreshTokenSingleton.getInstance();
      if (DEBUG_TRY) {
        logger.trace(...formatTrace('ApiServices - _tryOrRefresh - refresher:',{refreshing:refresher.refreshing,pendingRefreshAskers:refresher.pendingRefreshAskers}));
      }
      // if (refresher.refreshing) {
        // on a deja un refresh en cours, on attend un peu et on reessaye
        // setTimeout(
        //   async () => {
        //     if (state.accessToken !== accessToken) {
        //       logger.trace('ApiServices - _tryOrRefresh - access token has changed during request. Try again with the new one...');
        //       const newAccessToken:string = state.accessToken;
        //       // on reessaye l'action
        //       response = await request(newAccessToken);
        //       logger.trace('ApiServices - _tryOrRefresh - returned response',response);
        //       return response;
        //     }
        //     else {
        //       logger.trace('ApiServices - _tryOrRefresh - no accessToken change after waiting 300ms, abort');
        //       throw new Error('No parallel refresh request.');
        //     }
        //   },2000
        // );

      // }
      // else {
        const newAccessToken:string = await refresher.waitForRefresh({refreshToken,dispatch});
        // on reessaye l'action
        // OU BIEN est-elle auto relancee via le changement de state sur les token?
        response = await request(newAccessToken);
        if (DEBUG_TRY) {
          logger.trace(...formatTrace('ApiServices - _tryOrRefresh - (2) returned response',response));
        }
        return response;
      // }
    }
  }
  // on renvoie la response finale
  return response;
};

export interface I_COMMON_TOKENS_PARAMS {
  accessToken?: string,
  refreshToken?: string,
}
export interface I_COMMON_DELETE_PARAMS {
  accessToken?: string,
  refreshToken?: string,
  id: number,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
export interface I_COMMON_GET_ALL_PARAMS {
  accessToken?: string;
  refreshToken?: string;
  page?: number;
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
export interface I_COMMON_GET_ITEM_PARAMS {
  accessToken?: string,
  refreshToken?: string,
  id: number,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
export interface I_COMMON_IMPORT_PARAMS {
  accessToken?: string,
  refreshToken?: string,
  file?: File;
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
export interface I_COMMON_CREATE_PARAMS {
  accessToken: string,
  refreshToken: string,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
}
export interface I_COMMON_PUT_PARAMS {
  accessToken?: string,
  refreshToken?: string,
  dispatch: any; // on a besoin du dispatch pour store les tokens en cas de refresh
  state: any; // on a besoin du state pour comparer les tokens en cas de 401
  id: number; // id BDD de l'item à modifier
}
